import Title from 'Title';
import {handleApiError} from 'middleware/errorHandler';
import {useEffect} from 'react';
import {useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';
import {
  selectHasPermission,
  selectIsBootstrapped,
  selectIsLoggedIn,
  selectUserContext,
} from 'store/authSlice';
import {
  isSwitchingAccount,
  redirectToLoginWhenActive,
  shouldAttemptAutoLogin,
} from 'utils/autoLogin';
import errorLogger, {AuthError} from 'utils/errors';

const ProtectedRoute = ({component: Component, ...rest}) => {
  const location = useLocation();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isBootstrapped = useSelector(selectIsBootstrapped);
  const hasPermission = useSelector((state) =>
    selectHasPermission(state, location.pathname)
  );
  const user = useSelector(selectUserContext);

  useEffect(() => {
    if (!isBootstrapped) return;

    // Check authentication
    if (!isLoggedIn) {
      // Account switching clears auth and redirects to OneLogin itself;
      // skip the "Authentication Required" popup during that transition.
      if (isSwitchingAccount()) return;

      if (shouldAttemptAutoLogin()) {
        redirectToLoginWhenActive();
        return;
      }

      const authError = new AuthError('Authentication required', {
        authType: 'unauthenticated',
        status: 401,
        context: {
          route: location.pathname,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
        },
      });

      // Handle the error (will show appropriate UI)
      handleApiError(authError);
      return;
    }

    // Check authorization
    if (!hasPermission) {
      const authError = new AuthError(
        'Insufficient permissions for this resource',
        {
          authType: 'authorization',
          status: 403,
          context: {
            route: location.pathname,
            userPermissions: user?.permissions,
            userId: user?.sub,
            userEmail: user?.email,
            timestamp: new Date().toISOString(),
          },
        }
      );

      // Log the auth error with more context
      errorLogger.logAuthError(authError, {
        route: location.pathname,
        action: 'access_denied',
        userInfo: {
          id: user?.sub,
          email: user?.email,
          permissions: user?.permissions,
        },
      });

      // Handle the error (will show appropriate UI)
      handleApiError(authError);
    }
  }, [location, isBootstrapped, isLoggedIn, hasPermission, user]);

  // Wait for the /auth/me/ bootstrap check before rendering anything
  if (!isBootstrapped) return null;

  // Only render component if all checks pass
  if (!isLoggedIn || !hasPermission) {
    return null; // Error handler will show the appropriate UI
  }

  return (
    <>
      <Title />
      <Component {...rest} />
    </>
  );
};

export default ProtectedRoute;
